import { c as _c } from "react/compiler-runtime";
import './content-feed-gallery.scss';
import React from 'react';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { Image } from '@sola/ark-skippy/atoms/image/image.tsx';
import { Lightbox, LightboxTrigger } from '@sola/ark-skippy/molecules/lightbox/lightbox.tsx';
import type { ContentFeedMedia } from '../../organisms/content-feed-card/content-feed-card.tsx';

/** Returns the URL to display for a media item (thumbnail for video, direct URL for image). */
const getDisplayUrl = (media: ContentFeedMedia): string => media.type === 'video' ? media.thumbnailUrl || media.url : media.url;
interface LightboxItem {
  media: {
    image?: {
      url: string;
      alt: string;
      width: number;
      height: number;
    };
    video?: {
      url: string;
      thumbnailUrl?: string;
      width?: number;
      height?: number;
    };
  };
}
export interface ContentFeedGalleryProps {
  /** Up to 3 gallery images to display */
  galleryItems: ContentFeedMedia[];
  /** All image items formatted for the lightbox viewer */
  lightboxItems: LightboxItem[];
  /** Total number of photos (for "View all X photos" overlay) */
  totalPhotos: number;
  /** Whether to show "New Update" badge on first image */
  hasNewUpdate: boolean;
  /** Fallback alt text (entry title) */
  title: string;
  /** i18n function */
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Photo gallery with grid layout and lightbox support.
 * Displays 1 image full-width, 2 images stacked, or 3+ in a grid with overlay.
 */
export const ContentFeedGallery: React.FC<ContentFeedGalleryProps> = t0 => {
  const $ = _c(7);
  const {
    galleryItems,
    lightboxItems,
    totalPhotos,
    hasNewUpdate,
    title,
    t
  } = t0;
  if (galleryItems.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== galleryItems || $[1] !== hasNewUpdate || $[2] !== lightboxItems || $[3] !== t || $[4] !== title || $[5] !== totalPhotos) {
    t1 = <div className="p-comfy-md">{galleryItems.length >= 3 ? <div className="ContentFeedGallery__grid gap-snug-xl">{galleryItems.map((m, index) => <div key={`gallery-${m.url}`} className="ContentFeedGallery__grid-item"><Lightbox items={lightboxItems} startIndex={index}><LightboxTrigger className="w-full h-full" ariaLabel={t("my:contentFeed.openGallery")}><div className="ContentFeedGallery__image">{index === 0 && hasNewUpdate && <div className="ContentFeedGallery__badge"><Badge label={t("my:contentFeed.newUpdate")} iconBefore="announcement-01" color="brand" /></div>}{m.type === "video" && <div className="ContentFeedGallery__video-play"><ArkIcon iconName="play" /></div>}<Image image={{
                  url: getDisplayUrl(m),
                  alt: m.alt || title
                }} hasPlaceholder={true} aspectRatio="4-3" /></div></LightboxTrigger></Lightbox>{index === 2 && totalPhotos > 3 && <Lightbox items={lightboxItems} startIndex={0}><LightboxTrigger className="ContentFeedGallery__overlay label-potent label-md" ariaLabel={t("my:contentFeed.viewAllPhotos", {
              count: totalPhotos
            })}><span>{t("my:contentFeed.viewAllPhotos", {
                  count: totalPhotos
                })}</span></LightboxTrigger></Lightbox>}</div>)}</div> : <Lightbox items={lightboxItems} startIndex={0}><LightboxTrigger className="w-full" ariaLabel={t("my:contentFeed.openGallery")}><div className="ContentFeedGallery__image rounded-lg overflow-hidden">{hasNewUpdate && <div className="ContentFeedGallery__badge"><Badge label={t("my:contentFeed.newUpdate")} iconBefore="announcement-01" color="brand" /></div>}{galleryItems[0].type === "video" && <div className="ContentFeedGallery__video-play"><ArkIcon iconName="play" /></div>}<Image image={{
              url: getDisplayUrl(galleryItems[0]),
              alt: galleryItems[0].alt || title
            }} hasPlaceholder={true} aspectRatio="4-3" /></div></LightboxTrigger></Lightbox>}</div>;
    $[0] = galleryItems;
    $[1] = hasNewUpdate;
    $[2] = lightboxItems;
    $[3] = t;
    $[4] = title;
    $[5] = totalPhotos;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};